import React, { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useT } from '../i18n';
import { REACTIONS, shareHref } from '../firebase/chatService';
import ChatShareCard from './ChatShareCard';
import './ChatMessage.css';

// How far left you must drag before a swipe counts as "reply to this".
const SWIPE_ARM = 56;
// Past this the bubble stops following the finger — a rubber band, so the
// gesture has a felt end instead of the message sliding off the screen.
const SWIPE_MAX = 78;
// A drag steeper than this is the list scrolling, not a swipe. Checked once,
// on the first significant movement, so a gesture cannot change its mind
// halfway and start stealing the scroll.
const SWIPE_SLOPE = 1.0;
// Hold this long to open the message menu.
const HOLD_MS = 380;
// Movement over this cancels the hold — a scroll that happens to start with a
// still finger must not turn into a menu.
const HOLD_SLOP = 10;

// ── GEOMETRY OF THE OPEN MENU ────────────────────────────────────────────────
// JS owns these numbers and hands them to CSS as custom properties, rather than
// both files declaring them and drifting apart. Positioning needs the menu's
// height BEFORE it is rendered, so the height has to be computable — and a
// computed height that disagrees with the painted one puts the menu off screen.
const PICK_H = 52;     // the reaction row
const ROW_H = 44;      // one menu row
const MENU_PAD = 6;    // the menu box's own padding, top and bottom
const MENU_BDR = 1;    // its border, top and bottom
const GAP = 8;         // bubble → popover
const EDGE = 10;       // popover → screen edge

// ── ICONS ────────────────────────────────────────────────────────────────────
// Inline rather than a sprite: four glyphs used in one place, and each needs
// to inherit the row's colour so Delete comes out red without a second copy.
const Ico = ({ d, fill }) => (
  <svg viewBox="0 0 24 24" width="19" height="19" aria-hidden="true"
    fill={fill ? 'currentColor' : 'none'} stroke={fill ? 'none' : 'currentColor'}
    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    {d}
  </svg>
);
const IcoReply = <Ico d={<><path d="M9 14 4 9l5-5" /><path d="M4 9h9a7 7 0 0 1 7 7v4" /></>} />;
const IcoCopy = <Ico d={<><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V5a2 2 0 0 1 2-2h10" /></>} />;
const IcoEdit = <Ico d={<><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" /></>} />;
const IcoTrash = <Ico d={<><path d="M3 6h18" /><path d="M8 6V4h8v2" /><path d="M6 6l1 14h10l1-14" /></>} />;

// ── ONE MESSAGE ──────────────────────────────────────────────────────────────
// The bubble, plus everything you can do to it: swipe-to-reply, press-and-hold
// (or right-click) for the action menu, and the reactions themselves.
//
// Extracted from ChatThread because those gestures need per-message state and
// refs; keeping them in the list meant one component holding the gesture state
// of forty messages at once.
export default function ChatMessage({
  m, mine, quoted, myUid, chatId,
  onReply, onEdit, onDelete, onReact, onOpenImage,
}) {
  const { t } = useT();
  const [dx, setDx] = useState(0);          // live swipe offset, px (negative)
  // null when closed, otherwise the measured screen position of the two
  // popovers. Held as one piece of state so they can never be half-open.
  const [ctx, setCtx] = useState(null);

  const start = useRef(null);               // { x, y } of the current gesture
  const axis = useRef(null);                // 'x' | 'y', locked on first move
  const holdTimer = useRef(null);
  const rowRef = useRef(null);
  const bubbleRef = useRef(null);

  const clearHold = () => { clearTimeout(holdTimer.current); holdTimer.current = null; };
  const closeCtx = () => setCtx(null);
  useEffect(() => clearHold, []);

  const rows = [
    { key: 'reply', icon: IcoReply, label: t('chat_reply'), run: () => onReply(m) },
    // COPY TAKES THE LINK when there is no caption to take. A share card was a
    // pasted URL before it was a card, and "send me that link" is still the
    // commonest thing anyone wants from one.
    (m.text || m.share) && { key: 'copy', icon: IcoCopy, label: t('chat_copy'), run: () => copy() },
    mine && { key: 'edit', icon: IcoEdit, label: t('chat_edit'), run: () => onEdit(m) },
    mine && { key: 'del', icon: IcoTrash, label: t('delete_tooltip'), run: () => onDelete(m), danger: true },
  ].filter(Boolean);

  // ── OPENING THE MENU ───────────────────────────────────────────────────────
  //
  // The popovers are PORTALLED TO THE BODY and positioned in screen
  // coordinates. They used to be absolutely positioned inside the bubble, which
  // meant the thread's own scroll box clipped them — the reason this file
  // carried two "flip it the other way" classes and a routine to measure the
  // room above and below. Outside the scroll box there is nothing to clip
  // against and none of that is needed.
  const openCtx = () => {
    const el = bubbleRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const menuH = rows.length * ROW_H + MENU_PAD * 2 + MENU_BDR * 2;

    // THREE LAYOUTS, TRIED IN ORDER. The first that fits on screen wins.
    //
    //   A  reactions above the message, actions below it — the arrangement
    //      you want, whenever there is room for it.
    //   B  the whole stack above the message. This is the ordinary case for
    //      recent messages: they sit just above the composer, where the few
    //      dozen pixels below them cannot hold a menu.
    //   C  the whole stack below, for a message at the top of the thread.
    //
    // NONE OF THEM COVER THE MESSAGE, which matters beyond looks: the message
    // is lifted above the dimmer while its menu is open, so anything drawn
    // over it would end up behind it.
    const candidates = [
      { pick: r.top - GAP - PICK_H, menu: r.bottom + GAP },
      { pick: r.top - GAP - menuH - GAP - PICK_H, menu: r.top - GAP - menuH },
      { pick: r.bottom + GAP, menu: r.bottom + GAP + PICK_H + GAP },
    ];
    const fits = (c) => c.pick >= EDGE && c.menu + menuH <= vh - EDGE;

    let place = candidates.find(fits);
    if (!place) {
      // A menu taller than the room on either side of the message — only
      // reachable on a very short viewport. Pin the stack to the roomier half
      // and clamp; covering part of the message beats running off the screen.
      const above = r.top > vh - r.bottom;
      const menu = above
        ? Math.max(EDGE + PICK_H + GAP, r.top - GAP - menuH)
        : Math.min(r.bottom + GAP + PICK_H + GAP, vh - EDGE - menuH);
      place = { pick: menu - GAP - PICK_H, menu };
    }
    const pickTop = place.pick;
    const menuTop = place.menu;

    // Lift the message above the dimmer ONLY if nothing landed on top of it.
    // The lift is what keeps the message you are acting on lit, but it also
    // puts it above the menu — so in the last-resort placement, where the menu
    // has to cover part of the message, lifting would hide the menu behind it.
    const hits = (top, h) => top < r.bottom && top + h > r.top;
    const lift = !hits(pickTop, PICK_H) && !hits(menuTop, menuH);

    // Aligned to the message's own edge, so the menu reads as belonging to it.
    const side = mine
      ? { right: Math.max(EDGE, vw - r.right) }
      : { left: Math.max(EDGE, r.left) };

    if (navigator.vibrate) navigator.vibrate(8);
    setCtx({
      lift,
      // ROUNDED UP, never down. `getBoundingClientRect` returns a fractional
      // width, and handing that straight back as a CSS width can land a
      // sub-pixel short of what the text needs — enough to wrap the last word
      // onto its own line, so the copy does not match the message underneath
      // it. A pixel of slack costs nothing and cannot re-wrap.
      msg: { top: r.top, left: r.left, width: Math.ceil(r.width) + 1 },
      pick: { top: pickTop, ...side },
      menu: { top: menuTop, ...side },
    });
  };

  // Escape closes. So does anything that moves the message out from under the
  // menu — the popovers are pinned to the screen, not to the bubble, so a
  // scroll or a rotation would leave them pointing at nothing.
  //
  // BOTH NEED A THRESHOLD, and without one this closed itself on Android about
  // a second after it opened:
  //
  //   · SCROLL. The finger is still down when the menu appears, and the thread
  //     allows vertical panning (`touch-action: pan-y`), so the tiny drift of a
  //     hand holding still scrolls it by a pixel or two. That fired `scroll`,
  //     and the menu the hold had just opened shut again.
  //
  //   · RESIZE. Chrome's address bar collapses on its own and fires `resize`
  //     with no interaction at all.
  //
  // So: only a real scroll, and only a real resize. 8px is more than a held
  // hand wanders and less than any deliberate scroll; 120px is a keyboard or a
  // rotation rather than browser chrome.
  useEffect(() => {
    if (!ctx) return undefined;
    const scroller = rowRef.current?.closest('.ct-scroll');
    const fromTop = scroller ? scroller.scrollTop : 0;
    const fromH = window.innerHeight;

    const onKey = (e) => { if (e.key === 'Escape') closeCtx(); };
    const onScroll = () => {
      if (!scroller || Math.abs(scroller.scrollTop - fromTop) > 8) closeCtx();
    };
    const onResize = () => {
      if (Math.abs(window.innerHeight - fromH) > 120) closeCtx();
    };

    document.addEventListener('keydown', onKey);
    scroller?.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('keydown', onKey);
      scroller?.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
    };
  }, [ctx]);

  // ── gestures ───────────────────────────────────────────────────────────────
  // Pointer events rather than touch: one code path covers finger, pen and
  // mouse, so press-and-hold works on a laptop too.
  const onPointerDown = (e) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    start.current = { x: e.clientX, y: e.clientY };
    axis.current = null;
    clearHold();
    holdTimer.current = setTimeout(() => {
      openCtx();
      start.current = null;                 // the hold consumed this gesture
      setDx(0);
    }, HOLD_MS);
  };

  const onPointerMove = (e) => {
    if (!start.current) return;
    const mx = e.clientX - start.current.x;
    const my = e.clientY - start.current.y;

    if (Math.abs(mx) > HOLD_SLOP || Math.abs(my) > HOLD_SLOP) clearHold();

    // Lock the axis once, on the first movement worth judging. Without this a
    // mostly-vertical scroll that drifts sideways would start dragging the
    // bubble mid-flick.
    if (!axis.current && (Math.abs(mx) > 6 || Math.abs(my) > 6)) {
      axis.current = Math.abs(mx) > Math.abs(my) * SWIPE_SLOPE ? 'x' : 'y';
    }
    if (axis.current !== 'x') return;

    // LEFT ONLY. Dragging right does nothing, so the gesture cannot be started
    // by a rightward flick and finished leftwards.
    const pull = Math.min(0, mx);
    setDx(Math.max(-SWIPE_MAX, pull * 0.55));
  };

  const finishSwipe = () => {
    clearHold();
    if (axis.current === 'x' && dx <= -SWIPE_ARM * 0.55) onReply(m);
    start.current = null;
    axis.current = null;
    setDx(0);
  };

  const armed = dx <= -SWIPE_ARM * 0.55;

  // Tapping the one you already chose clears it — the parent decides that, so
  // the picker and the reaction chips below behave identically.
  const react = (emoji) => { closeCtx(); onReact(m, emoji); };

  // ── COPY ───────────────────────────────────────────────────────────────────
  // Two paths on purpose. `navigator.clipboard` needs a SECURE CONTEXT, so it
  // is simply absent over plain http — which is exactly how the app is reached
  // when testing from a phone on the LAN — and it rejects when the document is
  // not focused. The hidden-textarea fallback works in both cases.
  //
  // Failing silently is deliberate: a copy that did not happen is a minor
  // annoyance, an error dialog on top of a conversation is not.
  const copy = async () => {
    // The caption if there is one, otherwise the shared link — absolute, so it
    // is useful outside this app, which is the only reason to copy it.
    const link = m.share ? shareHref(m.share) : '';
    const text = m.text || (link ? `${window.location.origin}${link}` : '');
    if (!text) return;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return;
      }
    } catch (err) { /* fall through */ }
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.cssText = 'position:fixed;top:-1000px;opacity:0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    } catch (err) { /* nothing left to try */ }
  };

  // reactions: { uid: emoji } → [{ emoji, count, mine }]
  const grouped = Object.entries(m.reactions || {}).reduce((acc, [uid, emoji]) => {
    const row = acc.find(r => r.emoji === emoji);
    if (row) { row.count += 1; row.mine = row.mine || uid === myUid; }
    else acc.push({ emoji, count: 1, mine: uid === myUid });
    return acc;
  }, []);

  // The bubble's contents, rendered twice: in the thread, and — while the menu
  // is open — as a copy floating above the dimmer.
  const bubbleInner = (
    <>
      {/* THE QUOTE IS RENDERED FROM THE REAL MESSAGE, resolved by the parent
          from what is loaded — the reply itself stores only an id, so a quote
          can never say something that was not said. */}
      {m.replyTo && (
        <span className={`ct-quote${quoted ? '' : ' ct-quote--gone'}`}>
          {quoted ? quoted.text : t('chat_reply_gone')}
        </span>
      )}
      {/* THE PICTURE, at preview quality. Tapping it fetches the full
          rendition (see onOpenImage) — the thread never reads that, which is
          what keeps a conversation full of photos cheap to open. */}
      {m.image?.url && (
        <button
          type="button"
          className="ct-img-btn"
          onClick={(e) => { e.stopPropagation(); onOpenImage?.(m); }}
          aria-label={t('chat_img_open')}
        >
          <img
            className="ct-img"
            src={m.image.url}
            alt=""
            // The stored dimensions give the bubble its shape before the
            // picture decodes; without them the thread jumps as each one
            // arrives, which on a slow connection is every one of them.
            width={m.image.w || undefined}
            height={m.image.h || undefined}
            style={m.image.w && m.image.h
              ? { aspectRatio: `${m.image.w} / ${m.image.h}` }
              : undefined}
            draggable={false}
          />
        </button>
      )}
      {/* A SHARED PIECE OF WORK. `suppressed` while the message menu is open:
          the hold that opened it ends in a click, which would otherwise
          navigate away from the menu it just produced. */}
      {m.share && <ChatShareCard share={m.share} suppressed={!!ctx} chatId={chatId} />}
      {m.text}
      {/* INSIDE THE BUBBLE, not overlapping its corner. Hung off the outside
          they collided with the message: a second chip wrapped UPWARDS over
          the text, and a single one sat on top of the timestamp. In the flow
          they simply take their own row, and any number of them wrap the way
          a row of chips should. */}
      {grouped.length > 0 && (
        <span className="ct-reacts">
          {grouped.map(r => (
            <button
              key={r.emoji}
              type="button"
              className={`ct-react${r.mine ? ' ct-react--mine' : ''}`}
              onClick={() => onReact(m, r.emoji)}
              title={t('chat_react')}
            >
              <span aria-hidden="true">{r.emoji}</span>
              {r.count > 1 && <b>{r.count}</b>}
            </button>
          ))}
        </span>
      )}
      <time className="ct-time">
        {m.stamp}
        {m.editedAt && <span className="ct-edited"> · {t('chat_edited')}</span>}
      </time>
    </>
  );

  return (
    <li
      ref={rowRef}
      className={`ct-msg${mine ? ' ct-msg--mine' : ''}${armed ? ' ct-msg--armed' : ''}`}
    >
      {/* The reply arrow revealed behind the bubble as it slides. */}
      <span className="ct-swipe-hint" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor"
          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 14 4 9l5-5" />
          <path d="M4 9h9a7 7 0 0 1 7 7v4" />
        </svg>
      </span>

      <div
        className={`ct-msg-body${dx ? ' ct-msg-body--dragging' : ''}${ctx?.lift ? ' ct-msg-body--open' : ''}`}
        // `pan-y` lets the thread keep scrolling vertically while telling the
        // browser not to claim horizontal panning — without it the swipe fights
        // the compositor and the bubble stutters.
        style={{ touchAction: 'pan-y', ...(dx ? { transform: `translateX(${dx}px)` } : null) }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={finishSwipe}
        onPointerCancel={finishSwipe}
        onPointerLeave={finishSwipe}
        // RIGHT-CLICK IS THE SAME MENU. It also suppresses Android's own
        // long-press menu, which fires this event and would otherwise open its
        // text-selection callout on top of ours.
        onContextMenu={(e) => { e.preventDefault(); clearHold(); openCtx(); }}
      >
        {/* THE ⋯ IS FOR POINTER DEVICES (see the CSS). There is no press-and-
            hold habit with a mouse, and right-click is not where most people
            look first. It opens the same menu, anchored the same way. */}
        <span className="ct-actions">
          <button
            type="button"
            className="ct-act"
            aria-label={t('chat_actions')}
            title={t('chat_actions')}
            aria-expanded={!!ctx}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => (ctx ? closeCtx() : openCtx())}
          >
            <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
              <circle cx="5" cy="12" r="2.1" /><circle cx="12" cy="12" r="2.1" /><circle cx="19" cy="12" r="2.1" />
            </svg>
          </button>
        </span>

        <span className="ct-bubble" ref={bubbleRef}>{bubbleInner}</span>
      </div>

      {ctx && createPortal(
        <div
          className="ct-ctx"
          onPointerDown={closeCtx}
          // SUPPRESS THE NATIVE MENU, DO NOT CLOSE OURS.
          //
          // This closed too, and that is what shut the menu a moment after a
          // long press opened it on Android. The hold fires at 380ms and the
          // scrim renders under the still-pressed finger; Android then fires
          // `contextmenu` at around 500ms, which now lands on the scrim — so
          // the gesture that opened the menu immediately closed it again.
          //
          // preventDefault is still needed, and on its own: without it Android
          // draws its own text-selection menu on top of this one.
          onContextMenu={(e) => e.preventDefault()}
        >
          {/* THE MESSAGE, FLOATED ABOVE THE DIMMER.
              A copy, not the original. Lifting the original with a z-index
              cannot work: the thread sits inside `.cx-overlay`, which has both
              a z-index and a backdrop-filter and therefore forms a stacking
              context — everything inside it is trapped below the portalled
              dimmer no matter what number it is given. Drawn at the measured
              position of the real bubble, so it reads as the same message
              staying lit while the rest of the screen recedes. */}
          {ctx.lift && (
            <div
              className={`ct-ctx-msg ct-msg${mine ? ' ct-msg--mine' : ''}`}
              style={ctx.msg}
              aria-hidden="true"
            >
              <span className="ct-bubble">{bubbleInner}</span>
            </div>
          )}

          <div
            className="ct-ctx-pick"
            style={{ ...ctx.pick, height: PICK_H }}
            role="menu"
            aria-label={t('chat_react')}
            onPointerDown={(e) => e.stopPropagation()}
          >
            {REACTIONS.map(emoji => (
              <button
                key={emoji}
                type="button"
                className={`ct-ctx-emoji${(m.reactions || {})[myUid] === emoji ? ' ct-ctx-emoji--on' : ''}`}
                onClick={() => react(emoji)}
              >
                {emoji}
              </button>
            ))}
          </div>

          <div
            className="ct-ctx-menu"
            style={{ ...ctx.menu, '--ct-row': `${ROW_H}px`, '--ct-pad': `${MENU_PAD}px` }}
            role="menu"
            onPointerDown={(e) => e.stopPropagation()}
          >
            {/* EDIT AND DELETE ARE YOURS ONLY. Not a UI preference — the rules
                reject an edit or a delete of anyone else's message, so offering
                them would be buttons that always fail. */}
            {rows.map(r => (
              <button
                key={r.key}
                type="button"
                role="menuitem"
                className={`ct-ctx-item${r.danger ? ' ct-ctx-item--danger' : ''}`}
                onClick={() => { closeCtx(); r.run(); }}
              >
                {r.icon}
                <span>{r.label}</span>
              </button>
            ))}
          </div>
        </div>,
        document.body,
      )}
    </li>
  );
}
